import type { SequencingPlatformKey } from 'Generated/SequencingPlatform.js'

/** What one file's first reads say about the sequencer that produced them. */
export type DetectedSequencingPlatform = SequencingPlatformKey | 'UNKNOWN'

/**
 * Compressed bytes taken from the head of the file. Detection needs a handful of read headers, and
 * reading more would mean pulling a slice of every file of a 1300-file selection through the browser.
 */
const SNIFF_BYTES = 64 * 1024
const MAX_DECOMPRESSED_CHARS = 512 * 1024
const MAX_READS = 32
/** Files are sniffed in parallel; the bound keeps a large selection from opening 1300 reads at once. */
const SNIFF_CONCURRENCY = 8

const GZIP_MAGIC_0 = 0x1f
const GZIP_MAGIC_1 = 0x8b

/* underscores optional: MinKNOW writes `sample_id=`, older exports `sampleid=` */
const ONT_TAG = /(?:^|\s)(?:run_?id|sample_?id|flow_?cell_?id|(?:basecall_?)?model_?version_?id|parent_read_id|protocol_group_id)=/
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const ILLUMINA_HEADER_FIELDS = 7
const LEGACY_ILLUMINA_HEADER_FIELDS = 5

type Read = { header: string }

const isOntHeader = (header: string): boolean => ONT_TAG.test(header) || UUID.test(header.split(/\s+/)[0] ?? '')

const isIlluminaHeader = (header: string): boolean => {
  const id = header.split(/\s+/)[0] ?? ''
  const fields = id.split(':')
  if (fields.length === ILLUMINA_HEADER_FIELDS) return true
  /* pre-CASAVA-1.8 instruments wrote five fields with the index and mate glued on as `#0/1` */
  return fields.length === LEGACY_ILLUMINA_HEADER_FIELDS && /#[^/]*\/[12]$/.test(id)
}

/** Phase-locked on the first header, because a FASTQ quality line may itself begin with `@`. */
const parseFastq = (lines: string[]): Read[] => {
  const reads: Read[] = []
  const start = lines.findIndex((line) => line.startsWith('@'))
  if (start < 0) return reads
  for (let i = start; i + 1 < lines.length && reads.length < MAX_READS; i += 4) {
    if (!lines[i].startsWith('@')) break
    reads.push({ header: lines[i].slice(1) })
  }
  return reads
}

const parseFasta = (lines: string[]): Read[] => {
  const reads: Read[] = []
  let current: Read | null = null
  for (const line of lines) {
    if (line.startsWith('>')) {
      if (current) reads.push(current)
      if (reads.length >= MAX_READS) return reads
      current = { header: line.slice(1) }
    }
  }
  /* the open record is dropped — the head limit may have cut its sequence short */
  return reads
}

const parseReads = (text: string): Read[] => {
  const lines = text.split('\n')
  /* the head cut lands mid-line, and a truncated line is not evidence */
  if (!text.endsWith('\n')) lines.pop()
  const clean = lines.map((line) => (line.endsWith('\r') ? line.slice(0, -1) : line))
  const first = clean.find((line) => line.length > 0)
  if (first?.startsWith('@')) return parseFastq(clean)
  if (first?.startsWith('>')) return parseFasta(clean)
  return []
}

/**
 * The platform the given reads point at, or `UNKNOWN`.
 *
 * Deliberately narrow: a wrong answer routes the order to the wrong Airflow pipeline, so anything the
 * signals do not settle — no header evidence at all, or both platforms' evidence in one file — stays
 * `UNKNOWN` and leaves the decision to the user. GUI-exported FASTA legitimately lands here.
 */
export const detectSequencingPlatformInText = (text: string): DetectedSequencingPlatform => {
  const reads = parseReads(text)
  if (reads.length === 0) return 'UNKNOWN'

  let ont = 0
  let illumina = 0
  for (const read of reads) {
    if (isOntHeader(read.header)) ont++
    else if (isIlluminaHeader(read.header)) illumina++
  }

  if (ont > 0 && illumina === 0) return 'ONT'
  if (illumina > 0 && ont === 0) return 'ILLUMINA'
  return 'UNKNOWN'
}

const gunzipHead = async (bytes: Uint8Array<ArrayBuffer>): Promise<string> => {
  if (typeof DecompressionStream === 'undefined') return ''
  /* left unparameterised on purpose: DecompressionStream's writable side is typed BufferSource, a
     type-only DOM name that eslint's browser globals do not carry, so naming it trips no-undef */
  const source = new ReadableStream({
    start(controller) {
      controller.enqueue(bytes)
      controller.close()
    }
  })
  const reader = source.pipeThrough(new DecompressionStream('gzip')).getReader()
  const decoder = new TextDecoder()
  let text = ''
  try {
    while (text.length < MAX_DECOMPRESSED_CHARS) {
      const { done, value } = await reader.read()
      if (done) break
      text += decoder.decode(value, { stream: true })
    }
  } catch {
    /* a cut of a longer gzip member ends in an inflate error by construction; what came out before
       it is still valid, and it is all detection needs */
  }
  void reader.cancel().catch(() => undefined)
  return text
}

export const decodeHead = async (file: Blob, sniffBytes = SNIFF_BYTES): Promise<string> => {
  const head = new Uint8Array(await file.slice(0, sniffBytes).arrayBuffer())
  if (head[0] === GZIP_MAGIC_0 && head[1] === GZIP_MAGIC_1) return gunzipHead(head)
  return new TextDecoder().decode(head)
}

/** Never throws — an unreadable or unrecognised file is simply `UNKNOWN`. */
export const detectSequencingPlatform = async (file: Blob): Promise<DetectedSequencingPlatform> => {
  try {
    return detectSequencingPlatformInText(await decodeHead(file))
  } catch (error) {
    console.warn('Could not read the head of a selected file for sequencing-platform detection', error)
    return 'UNKNOWN'
  }
}

export const detectSequencingPlatforms = async <T extends Blob>(
  files: readonly T[]
): Promise<Map<T, DetectedSequencingPlatform>> => {
  const detected = new Map<T, DetectedSequencingPlatform>()
  let next = 0
  const sniff = async () => {
    for (let i = next++; i < files.length; i = next++) {
      detected.set(files[i], await detectSequencingPlatform(files[i]))
    }
  }
  await Promise.all(Array.from({ length: Math.min(SNIFF_CONCURRENCY, files.length) }, sniff))
  return detected
}

/**
 * The platform a set of per-file verdicts agrees on: the majority of the files that produced one,
 * ties going to the first seen. `UNKNOWN` files abstain rather than dissent.
 */
export const consensusSequencingPlatform = (
  detected: Iterable<DetectedSequencingPlatform>
): DetectedSequencingPlatform => {
  const votes = new Map<SequencingPlatformKey, number>()
  for (const platform of detected) {
    if (platform === 'UNKNOWN') continue
    votes.set(platform, (votes.get(platform) ?? 0) + 1)
  }
  let winner: DetectedSequencingPlatform = 'UNKNOWN'
  let best = 0
  for (const [platform, count] of votes) {
    if (count > best) {
      winner = platform
      best = count
    }
  }
  return winner
}
